// Log-argument serialisation for the console logger (MV-1740).
//
// The Report It ring buffer (services/reportIt/browserLogBuffer.ts) is text
// only, so anything a caller passes as a *second* argument —
// `logger.error('Error starting audio stream:', error)` — used to be dropped
// before it ever reached the buffer. Reports then showed a bare
// "Error starting audio stream:" with no error at all.
//
// Naive stringification does not fix that: `String(err)` on the Zoom SDK's
// reject payload yields "[object Object]", and `JSON.stringify(err)` on an
// Error yields "{}" because `name`/`message`/`stack` are non-enumerable. This
// module handles both, at any nesting depth, and is circular-reference safe.

// Enough frames to identify the throw site without flooding the 96 KB buffer.
const MAX_STACK_FRAMES = 4;
// Per-argument cap; a runaway payload must not evict the surrounding context.
const MAX_ARG_LENGTH = 2000;

/** True for anything that should be rendered as an error rather than as data. */
const isErrorLike = (value: unknown): value is Error => {
  if (value instanceof Error) {
    return true;
  }

  if (
    typeof DOMException !== 'undefined' &&
    value instanceof (DOMException as unknown as new () => unknown)
  ) {
    return true;
  }

  return Object.prototype.toString.call(value) === '[object Error]';
};

/** The `at …` frames of a stack, trimmed to the innermost few. */
const getStackFrames = (stack: unknown): string[] => {
  if (typeof stack !== 'string') {
    return [];
  }

  return stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('at '))
    .slice(0, MAX_STACK_FRAMES);
};

/**
 * Renders an error as `Name: message` plus its innermost stack frames.
 *
 * @param error - Error, DOMException, or any error-shaped value.
 * @returns A single multi-line string; never empty.
 */
export const describeError = (error: Error): string => {
  const name = error.name || 'Error';
  const message = error.message || String(error);
  const head = `${name}: ${message}`;

  // Zoom Video SDK rejects with Error-shaped values carrying `type` /
  // `reason` / `errorCode` — the only fields that identify the failure.
  const extras = ['type', 'reason', 'errorCode']
    .map((key) => {
      const value = (error as unknown as Record<string, unknown>)[key];
      return value === undefined ? null : `${key}=${String(value)}`;
    })
    .filter((entry): entry is string => entry !== null);

  const detail = extras.length > 0 ? `${head} (${extras.join(', ')})` : head;
  const frames = getStackFrames(error.stack);

  if (frames.length === 0) {
    return detail;
  }

  return [detail, ...frames.map((frame) => `    ${frame}`)].join('\n');
};

/**
 * A `JSON.stringify` replacer that unwraps nested errors and breaks cycles.
 *
 * @param seen - Set of objects already visited on the current stringify pass.
 * @returns A replacer suitable for `JSON.stringify`.
 */
const createReplacer = (seen: WeakSet<object>) => {
  return (_key: string, value: unknown): unknown => {
    if (isErrorLike(value)) {
      return describeError(value);
    }

    if (typeof value === 'bigint') {
      return `${value.toString()}n`;
    }

    if (typeof value === 'function') {
      return `[Function ${value.name || 'anonymous'}]`;
    }

    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }

      seen.add(value);
    }

    return value;
  };
};

/**
 * Renders one log argument as text, preserving error and object detail.
 *
 * Strings pass through untouched so ordinary log lines are unchanged.
 *
 * @param value - Any value handed to a logger method.
 * @returns The value as a string, truncated at {@link MAX_ARG_LENGTH}.
 */
export const serializeLogArg = (value: unknown): string => {
  let text: string;

  if (typeof value === 'string') {
    text = value;
  } else if (value === undefined) {
    text = 'undefined';
  } else if (value === null) {
    text = 'null';
  } else if (isErrorLike(value)) {
    text = describeError(value);
  } else if (typeof value === 'object') {
    try {
      text =
        JSON.stringify(value, createReplacer(new WeakSet())) ?? String(value);
    } catch {
      // Getters that throw, exotic host objects, BigInt edge cases.
      text = String(value);
    }
  } else {
    text = String(value);
  }

  if (text.length <= MAX_ARG_LENGTH) {
    return text;
  }

  return `${text.slice(0, MAX_ARG_LENGTH)}… [truncated]`;
};

/**
 * Renders the trailing log arguments as one space-separated string.
 *
 * @param args - Every argument after the primary message.
 * @returns The joined text, or an empty string when there is nothing to add.
 */
export const formatLogArgs = (args: unknown[]): string => {
  if (!args || args.length === 0) {
    return '';
  }

  return args.map(serializeLogArg).join(' ');
};
